import { useEffect, useState } from "react";
import axios from "axios";
import CatalogCrud, {
  TextField,
  NumberField,
  TextareaField,
  ArrayEditor,
  ActiveToggleRow,
  VioletSwitch,
} from "./CatalogCrud";

const BLANK = {
  name: "",
  localBackupDirectory: "",
  localBackupSearchPattern: "*.bak",
  maxBackupAgeHours: 24,
  masterConnectionString: "",
  trustServerCertificate: true,
  postRestoreStoredProcedure: "",
  forbiddenDatabaseNames: [],
  postRestoreMigrationInitialDelaySeconds: 90,
  postRestoreMigrationRetryCount: 3,
  postRestoreMigrationRetryDelaySeconds: 30,
  s3ConfigurationId: null,
  isActive: true,
};

function snapshotFrom(item) {
  return {
    name: item.name ?? "",
    localBackupDirectory: item.localBackupDirectory ?? "",
    localBackupSearchPattern: item.localBackupSearchPattern ?? "*.bak",
    maxBackupAgeHours: item.maxBackupAgeHours ?? 24,
    masterConnectionString: item.masterConnectionString ?? "",
    trustServerCertificate: item.trustServerCertificate !== false,
    postRestoreStoredProcedure: item.postRestoreStoredProcedure ?? "",
    forbiddenDatabaseNames: Array.isArray(item.forbiddenDatabaseNames)
      ? [...item.forbiddenDatabaseNames]
      : [],
    postRestoreMigrationInitialDelaySeconds: item.postRestoreMigrationInitialDelaySeconds ?? 0,
    postRestoreMigrationRetryCount: item.postRestoreMigrationRetryCount ?? 0,
    postRestoreMigrationRetryDelaySeconds: item.postRestoreMigrationRetryDelaySeconds ?? 0,
    s3ConfigurationId: item.s3ConfigurationId ?? null,
    isActive: !!item.isActive,
  };
}

function validate(snap) {
  if (!snap.name.trim()) return "Informe o nome.";
  if (!snap.localBackupDirectory.trim()) return "Informe o diretório de backup local.";
  return null;
}

export default function DbRestoreConfigList() {
  const [s3Configs, setS3Configs] = useState([]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API}/admin/s3-configurations`)
      .then((res) => setS3Configs(res.data || []))
      .catch(() => setS3Configs([]));
  }, []);

  return (
    <CatalogCrud
      title="Database Restore Configurations"
      description="Perfis de restore usados quando um ambiente solicita restauração de banco."
      endpoint="/admin/database-restore-configurations"
      itemSubtitle={(item) => item.localBackupDirectory || null}
      blank={BLANK}
      snapshotFrom={snapshotFrom}
      validate={validate}
      renderForm={({ form, update, saving }) => (
        <>
          <TextField
            label="Nome"
            value={form.name}
            onChange={(v) => update("name", v)}
            placeholder="Ex: Restore Qualitycert"
            disabled={saving}
            required
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <TextField
                label="Local backup directory"
                value={form.localBackupDirectory}
                onChange={(v) => update("localBackupDirectory", v)}
                placeholder="C:\\Backups\\…"
                disabled={saving}
                required
                mono
              />
            </div>
            <TextField
              label="Search pattern"
              value={form.localBackupSearchPattern}
              onChange={(v) => update("localBackupSearchPattern", v)}
              placeholder="*.bak"
              disabled={saving}
              mono
            />
            <NumberField
              label="Max backup age (horas)"
              value={form.maxBackupAgeHours}
              onChange={(v) => update("maxBackupAgeHours", v)}
              min={0}
              disabled={saving}
            />
            <div className="sm:col-span-2">
              <TextareaField
                label="Master connection string"
                value={form.masterConnectionString}
                onChange={(v) => update("masterConnectionString", v)}
                placeholder="Server=…;Database=master;User ID=…;Password=…"
                rows={2}
                disabled={saving}
              />
            </div>
            <TextField
              label="Post-restore stored procedure"
              value={form.postRestoreStoredProcedure}
              onChange={(v) => update("postRestoreStoredProcedure", v)}
              placeholder="sp_homologa"
              disabled={saving}
              mono
            />
          </div>

          <label className={`flex items-start justify-between gap-4 p-4 rounded-xl ring-1 transition cursor-pointer ${
            form.trustServerCertificate ? "bg-violet-50/60 ring-violet-200" : "bg-slate-50/50 ring-slate-200"
          }`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800">Trust server certificate</p>
              <p className="text-xs text-slate-500 mt-0.5">Confiar no certificado do servidor SQL mesmo quando não validado.</p>
            </div>
            <VioletSwitch
              checked={form.trustServerCertificate}
              onChange={(v) => update("trustServerCertificate", v)}
              disabled={saving}
            />
          </label>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Databases proibidos
            </label>
            <ArrayEditor
              items={form.forbiddenDatabaseNames}
              onChange={(v) => update("forbiddenDatabaseNames", v)}
              placeholder="nome_do_banco"
              disabled={saving}
            />
            <p className="mt-1 text-[11px] text-slate-500">Nomes que não podem ser sobrescritos pelo restore.</p>
          </div>

          <div className="pt-3 border-t border-slate-100">
            <h3 className="text-sm font-bold text-slate-800 mb-0.5">Migrations pós-restore</h3>
            <p className="text-xs text-slate-500 mb-3">Controle de execução de migrations após o restore do banco.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <NumberField
                label="Delay inicial (s)"
                value={form.postRestoreMigrationInitialDelaySeconds}
                onChange={(v) => update("postRestoreMigrationInitialDelaySeconds", v)}
                min={0}
                disabled={saving}
              />
              <NumberField
                label="Retries"
                value={form.postRestoreMigrationRetryCount}
                onChange={(v) => update("postRestoreMigrationRetryCount", v)}
                min={0}
                disabled={saving}
              />
              <NumberField
                label="Retry delay (s)"
                value={form.postRestoreMigrationRetryDelaySeconds}
                onChange={(v) => update("postRestoreMigrationRetryDelaySeconds", v)}
                min={0}
                disabled={saving}
              />
            </div>
          </div>

          <div className="pt-3 border-t border-slate-100">
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              S3 Configuration
            </label>
            <select
              value={form.s3ConfigurationId ?? ""}
              onChange={(e) =>
                update("s3ConfigurationId", e.target.value === "" ? null : Number(e.target.value))
              }
              disabled={saving}
              className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition disabled:opacity-60"
            >
              <option value="">— Sem S3 —</option>
              {s3Configs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.isActive === false ? " (inativo)" : ""}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-slate-500">Vincule um bucket para baixar backups pela nuvem antes do restore.</p>
          </div>

          <ActiveToggleRow
            checked={form.isActive}
            onChange={(v) => update("isActive", v)}
            disabled={saving}
            label="Configuração ativa"
            description="Desative para ocultar esta configuração da seleção em novos ambientes."
          />
        </>
      )}
    />
  );
}
